import type { AnalysisData, PostureClassificationResult } from './types';
import type { CustomLandmarks, CaptureSequence } from '@/components/posture-analysis/types';
import { getEvaluationRules } from '../evaluation-rules-load';
import type { PostureOrientationStrategyId } from './strategies/posture-orientation-strategy-id';
import { normalizePostureOrientationStrategyId } from './strategies/posture-orientation-strategy-id';
import { hipDisplacementHorizontalStrategy } from './strategies/hip-displacement-horizontal-strategy';
import { hipDisplacementHorizontalVisualPatternRecognitionStrategy } from './strategies/hip-displacement-horizontal-visual-pattern-recognition-strategy';
import { hipDisplacementHorizontalVetoExclusionStrategy } from './strategies/hip-displacement-horizontal-veto-exclusion-strategy';
import { clinicalScoreRulesStrategy } from './strategies/clinical-score-rules-strategy';
import { indicatorPointScoreVprOverrideStrategy } from './strategies/indicator-point-score-vpr-override-strategy';
import { indicatorPointScoreVprStrategy } from './strategies/indicator-point-score-vpr-strategy';
import { simplifiedAutoPilotStrategy } from './strategies/simplified-auto-pilot-strategy';
import { adaptiveThresholdVprOverrideStrategy } from './strategies/adaptive-threshold-vpr-override-strategy';
import { adaptiveThresholdVprOverrideV2Strategy } from './strategies/adaptive-threshold-vpr-override-strategy-v2';
import { adaptiveThresholdVprOverrideV3Strategy } from './strategies/adaptive-threshold-vpr-override-strategy-v3';
import { adaptiveThresholdVprOverrideV4Strategy } from './strategies/adaptive-threshold-vpr-override-strategy-v4';
import { adaptiveThresholdVprOverrideV5Strategy } from './strategies/adaptive-threshold-vpr-override-strategy-v5';
import { adaptiveThresholdVprOverrideV6Strategy } from './strategies/adaptive-threshold-vpr-override-strategy-v6';
import type { PostureClassificationStrategyInput } from './strategies/posture-orientation-strategy-types';
import { getFrontalCoronalMetrics, getFrontalView, parseLateralPostureMetrics } from './strategies/posture-analysis-metrics-parse';
import { getSignedFromDirection } from './strategies/posture-analysis-metrics-parse';
import { buildAsisPsisLineForSide } from './strategies/posture-strategy-context';
import {
  buildLateralizationDebugInputs,
  logDisplaySignalsDebug,
  logLateralizationDecisionDebug,
  logPostureClassificationDebug,
} from './strategies/posture-classification-debug';
import { buildClassificationSignals } from './build-classification-signals';

const DEBUG_LOG_PREFIX = '[computePostureDiagnoses]';
const evaluationRules = () => getEvaluationRules() as any;

const STRATEGY_REGISTRY = {
  hipDisplacementHorizontal: hipDisplacementHorizontalStrategy,
  hipDisplacementHorizontalVisualPatternRecognition: hipDisplacementHorizontalVisualPatternRecognitionStrategy,
  hipDisplacementHorizontalVetoExclusion: hipDisplacementHorizontalVetoExclusionStrategy,
  clinicalScoreRules: clinicalScoreRulesStrategy,
  indicatorPointScoreVprOverride: indicatorPointScoreVprOverrideStrategy,
  indicatorPointScoreVpr: indicatorPointScoreVprStrategy,
  simplifiedAutoPilot: simplifiedAutoPilotStrategy,
  adaptiveThresholdVprOverride: adaptiveThresholdVprOverrideStrategy,
  adaptiveThresholdVprOverrideV2: adaptiveThresholdVprOverrideV2Strategy,
  adaptiveThresholdVprOverrideV3: adaptiveThresholdVprOverrideV3Strategy,
  adaptiveThresholdVprOverrideV4: adaptiveThresholdVprOverrideV4Strategy,
  adaptiveThresholdVprOverrideV5: adaptiveThresholdVprOverrideV5Strategy,
  adaptiveThresholdVprOverrideV6: adaptiveThresholdVprOverrideV6Strategy,
} as const;

export interface ComputePostureDiagnosesOptions {
  strategyId?: PostureOrientationStrategyId | string | null;
  /** Optional live capture payload so ASIS/PSIS can refine geometry without new metric strings. */
  capturedForStrategy?: Partial<Record<CaptureSequence, { custom?: CustomLandmarks } | null>>;
  strategyInput?: Partial<PostureClassificationStrategyInput> | null;
}

export function computePostureDiagnoses(
  analysisData: AnalysisData,
  clientSex: 'Male' | 'Female' | 'Unknown' = 'Unknown',
  options?: ComputePostureDiagnosesOptions
): PostureClassificationResult | null {
  const strategyId = normalizePostureOrientationStrategyId(options?.strategyId ?? undefined);
  const strategy = STRATEGY_REGISTRY[strategyId as keyof typeof STRATEGY_REGISTRY];

  const rules = evaluationRules().posture.thresholds;
  const parsedForLateral = parseLateralPostureMetrics(analysisData, {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  });
  if (!parsedForLateral) return null;

  const viewSide = parsedForLateral.viewSide;
  const avgCvAngle = parsedForLateral.avgCvAngle;

  const latAngleThreshold = rules.latAngleThresholdDeg;
  const latShiftThreshold = rules.latShiftThresholdCm;
  const cvAngleThreshold = rules.cvAngleFhpThresholdDeg;

  const frontalView = getFrontalView(analysisData);
  const { headTilt, shoulderLevel, hipLevel, pelvicShift } = getFrontalCoronalMetrics(
    analysisData,
    frontalView
  );

  const strategyInput: PostureClassificationStrategyInput = {
    analysisData,
    clientSex,
    ...(options?.strategyInput?.sagittal ? { sagittal: options.strategyInput.sagittal } : {}),
    ...(options?.strategyInput?.sopCompletionRatio !== undefined
      ? { sopCompletionRatio: options.strategyInput.sopCompletionRatio }
      : {}),
    ...(options?.strategyInput?.sopTablePosture
      ? { sopTablePosture: options.strategyInput.sopTablePosture }
      : {}),
    ...(options?.strategyInput?.practitionerObservations
      ? { practitionerObservations: options.strategyInput.practitionerObservations }
      : {}),
    ...(options?.strategyInput?.sopMetrics
      ? { sopMetrics: options.strategyInput.sopMetrics }
      : {}),
  };

  const asisLine =
    viewSide
      ? buildAsisPsisLineForSide(
          viewSide,
          options?.strategyInput?.sagittal?.custom ??
            options?.capturedForStrategy?.[viewSide]?.custom
        )
      : null;

  const strategyResult = strategy.classify(strategyInput, {
    frontalView,
    asisPsisLine: asisLine,
  });
  if (!strategyResult) return null;

  const display = evaluationRules().posture.display;
  const sopTablePattern = options?.strategyInput?.sopTablePosture?.pattern ?? null;
  const imageOrientation = strategyResult.orientation;
  const effectiveOrientation: 'Swayback' | 'Anterior Pelvic Tilt' | 'Stacked' =
    sopTablePattern === 'SB'
      ? 'Swayback'
      : sopTablePattern === 'APT'
        ? 'Anterior Pelvic Tilt'
        : imageOrientation;

  const picturePattern =
    strategyResult.displayPattern ??
    (imageOrientation === 'Swayback'
      ? display.patterns.sb
      : imageOrientation === 'Anterior Pelvic Tilt'
        ? display.patterns.apt
        : imageOrientation === 'Anterior Pelvic Tilt with Posterior Lower Compression'
          ? display.patterns.aptPlc
          : imageOrientation === 'Stacked'
            ? display.patterns.stacked
            : display.patterns.stacked);

  const pattern =
    sopTablePattern === 'SB'
      ? display.patterns.sb
      : sopTablePattern === 'APT'
        ? display.patterns.apt
        : strategyResult.displayPattern ??
          (imageOrientation === 'Swayback'
            ? display.patterns.sb
            : imageOrientation === 'Anterior Pelvic Tilt'
              ? display.patterns.apt
              : imageOrientation === 'Anterior Pelvic Tilt with Posterior Lower Compression'
                ? display.patterns.aptPlc
                : imageOrientation === 'Stacked'
                  ? display.patterns.stacked
                  : display.patterns.stacked);

  const strategyMetaLine = `[${strategyResult.strategyDisplayName.trim()}] ${imageOrientation} — classification confidence ${strategyResult.confidencePct}% (${strategyResult.matchedPoints}/${strategyResult.maxPoints} pts)`;
  const evidenceLines = strategyResult.evidence;

  let lat = 'Right (Standard)';
  let latReason: string[] = [];
  let latScore = 0;
  const latActiveSignals: string[] = [];

  let calcL: number | undefined;
  let calcR: number | undefined;

  if (headTilt < -latAngleThreshold) {
    latScore += 1;
    latActiveSignals.push('lat.head.tiltLeft');
    latReason.push('Head Side-bent Left (Right Ear Higher)');
  } else if (headTilt > latAngleThreshold) {
    latScore -= 1;
    latActiveSignals.push('lat.head.tiltRight');
    latReason.push('Head Side-bent Right (Left Ear Higher)');
  }

  if (shoulderLevel > latAngleThreshold) {
    latScore += 1;
    latActiveSignals.push('lat.shoulder.rightLower');
    latReason.push('Right Shoulder Lower');
  } else if (shoulderLevel < -latAngleThreshold) {
    latScore -= 1;
    latActiveSignals.push('lat.shoulder.leftLower');
    latReason.push('Left Shoulder Lower');
  }

  if (hipLevel > latAngleThreshold) {
    latScore += 1;
    latActiveSignals.push('lat.hip.rightLower');
    latReason.push('Right Hip Lower');
  } else if (hipLevel < -latAngleThreshold) {
    latScore -= 1;
    latActiveSignals.push('lat.hip.leftLower');
    latReason.push('Left Hip Lower');
  }

  if (pelvicShift < -latShiftThreshold) {
    latScore += 1;
    latActiveSignals.push('lat.pelvic.shiftRight');
    latReason.push('Pelvic Shift Right');
  } else if (pelvicShift > latShiftThreshold) {
    latScore -= 1;
    latActiveSignals.push('lat.pelvic.shiftLeft');
    latReason.push('Pelvic Shift Left');
  }

  let footLatScore = 0;
  let footEvidenceCount = 0;
  if (analysisData['Posterior']) {
    calcL = getSignedFromDirection(analysisData, 'Posterior', 'Calcaneus tilt (L)', 'Inv', 'Ev');
    calcR = getSignedFromDirection(analysisData, 'Posterior', 'Calcaneus tilt (R)', 'Inv', 'Ev');

    if (isFinite(calcL) && calcL !== 0) {
      footLatScore += calcL < 0 ? 1 : -1;
      footEvidenceCount += 1;
    }
    if (isFinite(calcR) && calcR !== 0) {
      footLatScore += calcR > 0 ? 1 : -1;
      footEvidenceCount += 1;
    }

    if (footEvidenceCount > 0) {
      latActiveSignals.push('lat.foot.posteriorCalc');
      const weighted = footLatScore * 0.75;
      latScore += weighted;
      const lTxt = isFinite(calcL) ? `${Math.abs(calcL).toFixed(1)}° ${calcL >= 0 ? 'Inv' : 'Ev'}` : '—';
      const rTxt = isFinite(calcR) ? `${Math.abs(calcR).toFixed(1)}° ${calcR >= 0 ? 'Inv' : 'Ev'}` : '—';
      latReason.push(`Foot Presentation (Posterior): L Calc ${lTxt}, R Calc ${rTxt}`);
    }
  }

  logPostureClassificationDebug(
    DEBUG_LOG_PREFIX,
    'lateralization inputs (UI-mapped)',
    buildLateralizationDebugInputs({
      frontalView,
      headTilt,
      shoulderLevel,
      hipLevel,
      pelvicShift,
      latAngleThreshold,
      latShiftThreshold,
      hasPosterior: !!analysisData['Posterior'],
      calcL,
      calcR,
    })
  );

  if (latScore > 0) {
    lat = 'Right (Standard)';
  } else if (latScore < 0) {
    lat = 'Left (Reversed)';
  } else {
    lat = 'Balanced';
    if (!analysisData['Anterior'] && !analysisData['Posterior']) {
      latReason.push('Awaiting Anterior or Posterior view for lateralization analysis');
    } else {
      latReason.push('Normal balanced lateral alignment');
    }
  }

  const confidenceScore = Math.abs(latScore);
  const lateralizationConfidencePct = Math.min(100, Math.round(confidenceScore * 25));
  const groundingConfidencePct = Math.min(100, Math.round(confidenceScore * 20));
  const groundingSide =
    lat === 'Balanced' ? undefined : ((lat === 'Left (Reversed)' ? 'Left' : 'Right') as 'Left' | 'Right');

  logLateralizationDecisionDebug(DEBUG_LOG_PREFIX, {
    activeSignals: latActiveSignals,
    latScore,
    lateralized: lat,
    latReason,
    lateralizationConfidencePct,
    groundingSide,
    groundingConfidencePct,
  });

  let picOrientationConfidence = Math.round(strategyResult.confidencePct * 0.6);
  const picturePatternReason: string[] = [strategyMetaLine, ...evidenceLines];

  const integratedPatternReason: string[] = [];
  if (sopTablePattern === 'SB' || sopTablePattern === 'APT') {
    const sopLabel = sopTablePattern === 'SB' ? 'Swayback' : 'Anterior Pelvic Tilt';
    integratedPatternReason.push(
      `SoP table tests indicate ${sopLabel} (primary); image-based strategy read: ${imageOrientation} (${strategyResult.confidencePct}% confidence).`
    );
  }
  integratedPatternReason.push(strategyMetaLine, ...evidenceLines);

  const asisLineWarning = 'ASIS/PSIS needs to be set manually by the practitioner for best results';
  if (!asisLine) {
    picOrientationConfidence = Math.max(0, picOrientationConfidence - 20);
    picturePatternReason.push(asisLineWarning);
    integratedPatternReason.push(asisLineWarning);
  }

  const buildSignalsForOrientation = (
    orientation: typeof imageOrientation,
    evidence: string[]
  ): string[] =>
    buildClassificationSignals({
      orientation,
      orientationEvidence: evidence,
      lateralReasons: latReason,
      stackedLabel: display.patterns.stacked,
      forwardHeadDeg: avgCvAngle,
      forwardHeadThresholdDeg: cvAngleThreshold,
      lateralized: lat === 'Balanced' ? undefined : lat,
    });

  const pictureSignals = buildSignalsForOrientation(imageOrientation, evidenceLines);
  const signals = buildSignalsForOrientation(effectiveOrientation, evidenceLines);

  logDisplaySignalsDebug(DEBUG_LOG_PREFIX, {
    activeSignals: [],
    displayStrings: signals,
    pattern,
    orientation: strategyResult.orientation,
  });

  const picLatConf = Math.min(60, Math.round(confidenceScore * 25 * 0.6));
  const picGrdConf = Math.min(60, Math.round(confidenceScore * 20 * 0.6));

  const sopRatio = options?.strategyInput?.sopCompletionRatio ?? 0;
  const sopConf = Math.round(sopRatio * 40);

  const orientationConfidencePct = Math.min(100, picOrientationConfidence + sopConf);
  const integratedLatConfPct = Math.min(100, picLatConf + sopConf);
  const integratedGrdConfPct = Math.min(100, picGrdConf + sopConf);

  const reasoningShort = {
    global: strategyResult.evidence.slice(0, 2).map(e => e.replace(/^\[\+\d+\]\s*/, '')).join(' • ') || 'Algorithm determination',
    lateral: latReason.slice(0, 2).join(' • ') || 'Analysis indicates standard grounding',
    grounding: latReason.slice(0, 2).join(' • ') || 'Analysis indicates standard grounding',
  };

  const goals = evaluationRules().posture.display.goals as {
    sb: { phase1: string; metrics: string[] };
    apt: { phase1: string; metrics: string[] };
    stacked: { phase1: string; metrics: string[] };
    aptPlc?: { phase1: string; metrics: string[] };
  };

  const pictureIsSB = imageOrientation === 'Swayback';
  const pictureIsStacked = imageOrientation === 'Stacked';
  const pictureIsAptPlc = imageOrientation === 'Anterior Pelvic Tilt with Posterior Lower Compression';

  const integratedIsSB = effectiveOrientation === 'Swayback';
  const integratedIsStacked = effectiveOrientation === 'Stacked';
  const integratedIsAptPlc = effectiveOrientation === 'Anterior Pelvic Tilt with Posterior Lower Compression';

  const pictureGoals = pictureIsSB
    ? goals.sb
    : pictureIsStacked
    ? goals.stacked
    : pictureIsAptPlc
    ? (goals.aptPlc || goals.apt)
    : goals.apt;

  const integratedGoals = integratedIsSB
    ? goals.sb
    : integratedIsStacked
    ? goals.stacked
    : integratedIsAptPlc
    ? (goals.aptPlc || goals.apt)
    : goals.apt;

  const picturePosture = {
    pattern: picturePattern,
    lateralized: lat === 'Balanced' ? undefined : lat,
    lateralizationConfidencePct: picLatConf,
    groundingSide:
      lat === 'Balanced' ? undefined : ((lat === 'Left (Reversed)' ? 'Left' : 'Right') as 'Left' | 'Right'),
    groundingConfidencePct: picGrdConf,
    orientationConfidencePct: picOrientationConfidence,
    classificationStrategyId: strategyResult.strategyId,
    classificationStrategyLabel: strategyResult.strategyDisplayName.trim(),
    signals: pictureSignals,
    phase1Goal: pictureGoals.phase1,
    keyMetrics: pictureGoals.metrics,
    reasoning: {
      global: picturePatternReason,
      lateral: latReason,
    },
    reasoningShort,
  };

  return {
    pattern,
    lateralized: lat === 'Balanced' ? undefined : lat,
    lateralizationConfidencePct: integratedLatConfPct,
    groundingSide,
    groundingConfidencePct: integratedGrdConfPct,
    orientationConfidencePct,
    classificationStrategyId: strategyResult.strategyId,
    classificationStrategyLabel: strategyResult.strategyDisplayName.trim(),
    signals,
    phase1Goal: integratedGoals.phase1,
    keyMetrics: integratedGoals.metrics,
    reasoning: {
      global: integratedPatternReason,
      lateral: latReason,
    },
    reasoningShort,
    picturePosture,
  };
}
